import React, { useEffect, useMemo, useState } from "react";
import {
  Button, Form, Input, Modal, Select, Space, Tag, Tooltip, message as antdMessage,
} from "antd";
import {
  CheckOutlined, FileAddOutlined, RedoOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";

import * as chatbotApi from "api/chatbot";
import { CHAT_STATUS_COLOR } from "./constants";
import { normalizeChatMessage } from "./ChatMessage";

const PRIORITIES = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
  { value: "CRITICAL", label: "Critical" },
];

const errorText = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return fallback;
  return data.error || data.detail || fallback;
};

// Employees are needed by every chat thread header; fetch them once per page load.
let employeesPromise = null;
const loadEmployeesOnce = () => {
  if (!employeesPromise) {
    employeesPromise = chatbotApi
      .loadEmployees()
      .then((response) => chatbotApi.rowsOf(response))
      .catch((error) => {
        employeesPromise = null;
        throw error;
      });
  }
  return employeesPromise;
};

/**
 * Thread-header actions for a website chat session: assign, resolve / reopen,
 * and turn the chat into a support ticket.
 *
 * `onChanged(patch)` receives the fields to merge into the conversation row, so
 * the list and header update without waiting for the next refresh.
 */
const ChatSessionActions = ({ conversation, messages, onChanged }) => {
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [form] = Form.useForm();

  const ref = conversation?.id;
  const status = conversation?.status || "open";
  const resolved = status === "resolved";

  useEffect(() => {
    loadEmployeesOnce()
      .then(setEmployees)
      .catch(() => setEmployees([]));
  }, []);

  const firstVisitorText = useMemo(() => {
    const first = (messages || [])
      .map(normalizeChatMessage)
      .find((row) => row?.sender_type === "visitor" && row.body);
    return first ? first.body.slice(0, 120) : "";
  }, [messages]);

  const run = async (key, call, patch, successText) => {
    setBusy(key);
    try {
      const response = await call();
      const data = response?.data && typeof response.data === "object" ? response.data : {};
      onChanged?.({ ...patch, ...pickSession(data) });
      if (successText) antdMessage.success(successText);
    } catch (error) {
      antdMessage.error(errorText(error, "That did not work — try again"));
    }
    setBusy(null);
  };

  const assign = (employeeId) => {
    const employee = employees.find((row) => row.id === employeeId);
    run(
      "assign",
      () => chatbotApi.assignSession(ref, employeeId),
      {
        assigned_to: employeeId || null,
        assigned_to_name: employee ? chatbotApi.employeeLabel(employee) : null,
      },
      employeeId ? "Chat assigned" : "Chat unassigned"
    );
  };

  const openTicketModal = () => {
    setTicketOpen(true);
    form.setFieldsValue({
      title: firstVisitorText || `Website chat with ${
        conversation?.counterparty?.display_name || conversation?.visitor?.name || "visitor"
      }`,
      priority: "MEDIUM",
      category: undefined,
    });
    if (!categories.length) {
      chatbotApi
        .loadTicketCategories()
        .then((response) => setCategories(chatbotApi.rowsOf(response)))
        .catch(() => setCategories([]));
    }
  };

  const createTicket = async (values) => {
    setBusy("ticket");
    try {
      const response = await chatbotApi.createTicketFromSession(ref, values);
      const { ticket_id: ticketId, ticket_number: ticketNumber } = response.data || {};
      onChanged?.({ ticket_id: ticketId, ticket_number: ticketNumber });
      setTicketOpen(false);
      antdMessage.success(
        <span>
          Ticket {ticketNumber ? `#${ticketNumber}` : ""} created{" "}
          {ticketId ? <Link to={`/support/tickets/${ticketId}`}>Open</Link> : null}
        </span>
      );
    } catch (error) {
      antdMessage.error(errorText(error, "Could not create a ticket"));
    }
    setBusy(null);
  };

  if (!conversation) return null;

  return (
    <Space size={6} wrap>
      <Tag
        color={CHAT_STATUS_COLOR[status] || "default"}
        style={{ textTransform: "capitalize", margin: 0 }}
      >
        {status}
      </Tag>

      <Select
        size="small"
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Assign"
        style={{ width: 170 }}
        loading={busy === "assign"}
        value={conversation.assigned_to || undefined}
        onChange={(value) => assign(value)}
        options={[
          // Keep the current assignee visible even when they are outside the
          // first page of employees.
          ...(conversation.assigned_to
            && !employees.some((row) => row.id === conversation.assigned_to)
            ? [{
              value: conversation.assigned_to,
              label: conversation.assigned_to_name || "Assigned",
            }]
            : []),
          ...employees.map((row) => ({
            value: row.id,
            label: chatbotApi.employeeLabel(row),
          })),
        ]}
      />

      {resolved ? (
        <Button
          size="small"
          icon={<RedoOutlined />}
          loading={busy === "reopen"}
          onClick={() =>
            run("reopen", () => chatbotApi.reopenSession(ref), { status: "open" }, "Chat reopened")
          }
        >
          Reopen
        </Button>
      ) : (
        <Button
          size="small"
          icon={<CheckOutlined />}
          loading={busy === "resolve"}
          onClick={() =>
            run(
              "resolve",
              () => chatbotApi.resolveSession(ref),
              { status: "resolved" },
              "Chat resolved"
            )
          }
        >
          Resolve
        </Button>
      )}

      {conversation.ticket_number || conversation.ticket_id ? (
        <Tooltip title="Open the support ticket">
          <Link to={`/support/tickets/${conversation.ticket_id}`}>
            <Tag color="purple" style={{ margin: 0, cursor: "pointer" }}>
              Ticket #{conversation.ticket_number || "…"}
            </Tag>
          </Link>
        </Tooltip>
      ) : (
        <Button size="small" icon={<FileAddOutlined />} onClick={openTicketModal}>
          Create ticket
        </Button>
      )}

      <Modal
        title="Create support ticket"
        open={ticketOpen}
        onCancel={() => setTicketOpen(false)}
        onOk={() => form.submit()}
        okText="Create ticket"
        confirmLoading={busy === "ticket"}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={createTicket} preserve={false}>
          <Form.Item label="Title" name="title" rules={[{ required: true }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item label="Priority" name="priority">
            <Select options={PRIORITIES} />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category"
            extra="Leave empty to use the default category."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={categories.map((row) => ({ value: row.id, label: row.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

/** Session fields an action response may carry back. */
const SESSION_FIELDS = [
  "status", "assigned_to", "assigned_to_name", "ticket_id", "ticket_number",
];
export const pickSession = (data) =>
  SESSION_FIELDS.reduce((acc, key) => {
    if (!data || data[key] === undefined) return acc;
    // Guard against a `{status: "ok"}` style acknowledgement.
    if (key === "status" && !(data.status in CHAT_STATUS_COLOR)) return acc;
    if (key === "assigned_to" && data.assigned_to && typeof data.assigned_to === "object") {
      acc.assigned_to = data.assigned_to.id;
      return acc;
    }
    acc[key] = data[key];
    return acc;
  }, {});

export default ChatSessionActions;
